import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { TravelDetailSection, formatPaymentType } from 'components/travel/TravelDetailSection';
import { getQuillPlainLength } from 'components/RichTextEditor';

const isHtmlEmpty = (html) => getQuillPlainLength(html) === 0;

/** Turn API/enum values like `arrive_early` into "Arrive Early". */
const formatEnumLabel = (value) => {
    if (value === null || value === undefined || value === '') return value;
    if (typeof value !== 'string') return value;
    return value
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDisplayValue = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => formatEnumLabel(String(item))).join(', ');
    }
    if (typeof value === 'string' && /[_-]/.test(value)) {
        return formatEnumLabel(value);
    }
    return value;
};

const Field = ({ label, value, xs = 6, html = false, enumValue = false }) => {
    const displayValue = html
        ? value
        : (enumValue ? formatEnumLabel(value) : formatDisplayValue(value));
    const isEmpty = html
        ? isHtmlEmpty(value)
        : (value === null || value === undefined || value === '');
    return (
        <Grid item xs={xs}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            {html && !isEmpty ? (
                <Typography
                    variant="body2"
                    fontWeight={500}
                    component="div"
                    sx={{ '& p': { m: 0 }, '& ul, & ol': { pl: 2, my: 0.5 } }}
                    dangerouslySetInnerHTML={{ __html: value }}
                />
            ) : (
                <Typography variant="body2" fontWeight={500}>
                    {!isEmpty ? displayValue : '—'}
                </Typography>
            )}
        </Grid>
    );
};

const StatusChip = ({ value }) => {
    const color = value === 'active' ? 'success' : value === 'inactive' ? 'error' : 'default';
    return (
        <Chip
            label={formatEnumLabel(value || 'draft')}
            size="small"
            color={color}
        />
    );
};

const SectionCard = ({ title, subtitle, chips, children }) => (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 1.5 }}>
        {(title || chips?.length > 0) && (
            <>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap" mb={1}>
                    <Box>
                        {title && (
                            <MDTypography variant="body2" fontWeight="bold">
                                {title}
                            </MDTypography>
                        )}
                        {subtitle && (
                            <MDTypography variant="caption" color="text">
                                {subtitle}
                            </MDTypography>
                        )}
                    </Box>
                    {chips?.length > 0 && (
                        <MDBox display="flex" gap={0.75} flexWrap="wrap">
                            {chips}
                        </MDBox>
                    )}
                </MDBox>
                <Divider sx={{ mb: 1.5 }} />
            </>
        )}
        {children}
    </Box>
);

const EmptyState = ({ message }) => (
    <MDBox
        py={2}
        px={2}
        borderRadius="lg"
        sx={{ bgcolor: 'action.hover', border: '1px dashed', borderColor: 'divider' }}
    >
        <MDTypography variant="body2" color="text">
            {message}
        </MDTypography>
    </MDBox>
);

const StatPill = ({ label, value }) => (
    <MDBox
        px={1.5}
        py={1}
        borderRadius="lg"
        sx={{ bgcolor: 'rgba(255,255,255,0.15)', minWidth: 88 }}
    >
        <MDTypography variant="caption" color="white" opacity={0.85} display="block">
            {label}
        </MDTypography>
        <MDTypography variant="button" color="white" fontWeight="medium">
            {value}
        </MDTypography>
    </MDBox>
);

const formatDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
};

/** Parse "HH:MM", "HH:MM:SS" or "hh:mm AM/PM" into minutes since midnight. */
const timeToMinutes = (value) => {
    if (!value || typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const meridiem = match[3]?.toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
};

/** Use the provided duration; otherwise derive it from end_time - start_time, rounded to whole hours. */
const getActivityDuration = (service) => {
    const { duration, start_time: start, end_time: end } = service;
    if (duration !== null && duration !== undefined && duration !== '') return duration;

    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    if (startMin === null || endMin === null) return null;

    let diff = endMin - startMin;
    if (diff < 0) diff += 24 * 60; // activity crosses midnight
    return Math.round(diff / 60);
};

const ReviewStep = ({ allFormData, paymentTemplates = [], heroImage = '' }) => {
    const overview = allFormData.overview || {};
    const packages = allFormData.packages || {};
    const pricing = allFormData.pricing || {};
    const services = allFormData.services?.services || [];
    const departures = allFormData.departures?.departures || [];
    const allocations = allFormData.allocations?.allocations || [];
    const itineraries = allFormData.itineraries || {};
    const days = itineraries.days || [];
    const addons = allFormData.addons?.addons || [];
    const faqs = allFormData.faqs?.faqs || [];

    const basePrice = packages.base_price ?? pricing.base_price;
    const currency = (packages.currency || pricing.currency || '').toUpperCase();
    const paymentTemplateIds = packages.payment_templates || pricing.payment_templates || [];

    const resolveTemplateLabel = (id) => {
        const match = paymentTemplates.find((t) => String(t.id) === String(id));
        return match?.template_name || match?.name || match?.title || `Template #${id}`;
    };

    const tripTitle = overview.country || 'Untitled Destination';

    return (
        <Box>
            <MDBox mb={3}>
                <MDTypography variant="h5" fontWeight="medium">
                    Review &amp; Save
                </MDTypography>
                <MDTypography variant="body2" color="text" mt={0.5}>
                    Check every section below before saving your trip. You can go back to any step to make changes.
                </MDTypography>
            </MDBox>

            <Alert
                severity="info"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3, borderRadius: 2 }}
            >
                This is a read-only summary of everything you entered in the wizard.
            </Alert>

            {/* Summary strip */}
            <MDBox
                mb={3}
                p={2.5}
                borderRadius="lg"
                variant="gradient"
                bgColor="info"
                coloredShadow="info"
            >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <MDBox>
                        <MDTypography variant="h6" color="white" fontWeight="medium">
                            {tripTitle}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                            {overview.continent && (
                                <Chip
                                    label={overview.continent}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                                />
                            )}
                            {overview.country && (
                                <Chip
                                    label={overview.country}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', textTransform: 'capitalize' }}
                                />
                            )}
                            {packages.package_tier && (
                                <Chip
                                    label={formatEnumLabel(packages.package_tier)}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                                />
                            )}
                        </MDBox>
                        {basePrice != null && basePrice !== '' && (
                            <MDTypography variant="button" color="white" display="block" mt={1.5}>
                                From {currency} {basePrice}
                                {packages.duration ? ` · ${packages.duration} day${packages.duration > 1 ? 's' : ''}` : ''}
                            </MDTypography>
                        )}
                    </MDBox>
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                        <StatPill label="Trip Logistics" value={services.length} />
                        <StatPill label="Departures" value={departures.length} />
                        <StatPill label="Add-ons" value={addons.length} />
                        <StatPill label="FAQs" value={faqs.length} />
                    </MDBox>
                </MDBox>
            </MDBox>

            <Grid container spacing={3}>
                {/* Left column */}
                <Grid item xs={12} lg={8}>
                    <TravelDetailSection title="Destination Details">
                        <Grid container spacing={2}>
                            <Field label="Continent" value={overview.continent} />
                            <Field label="Country" value={overview.country} />
                            <Field label="Currency" value={overview.currency} />
                            <Field
                                label="Languages"
                                value={Array.isArray(overview.languages) ? overview.languages.join(', ') : overview.languages}
                            />
                            <Field label="Description" value={overview.description} xs={12} html />
                            {(overview.key_highlights || []).length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                        Key Highlights
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {(overview.key_highlights || []).map((item) => (
                                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                                <SectionCard title={item.name} subtitle={item.location}>
                                                    {item.image && (
                                                        <Box
                                                            component="img"
                                                            src={item.image}
                                                            alt={item.name}
                                                            sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                                                        />
                                                    )}
                                                </SectionCard>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </TravelDetailSection>

                    <TravelDetailSection title="Package">
                        <Grid container spacing={2}>
                            <Field label="Package Title" value={packages.package_title} />
                            <Field label="Tier" value={packages.package_tier} enumValue />
                            <Field label="Duration" value={packages.duration ? `${packages.duration} Days` : null} />
                            <Field
                                label="Cities"
                                value={Array.isArray(packages.city) ? packages.city.join(', ') : packages.city}
                            />
                            <Field label="Tags" value={packages.tags} enumValue />
                            <Field label="Sequence" value={packages.sequence} />
                            <Field label="Description" value={packages.package_description} xs={12} html />
                        </Grid>
                    </TravelDetailSection>

                    <TravelDetailSection title="Pricing &amp; Payments">
                        <Grid container spacing={2}>
                            <Field
                                label="Base Price"
                                value={basePrice != null && basePrice !== '' ? `${currency} ${basePrice}` : null}
                            />
                            <Field label="Currency" value={currency || null} />
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Payment Templates
                                </Typography>
                                {paymentTemplateIds.length === 0 ? (
                                    <Typography variant="body2" fontWeight={500}>—</Typography>
                                ) : (
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {paymentTemplateIds.map((id) => {
                                            const tpl = paymentTemplates.find((t) => String(t.id) === String(id));
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={resolveTemplateLabel(id)}
                                                    size="small"
                                                    variant="outlined"
                                                    title={tpl ? formatPaymentType(tpl.payment_type) : undefined}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </TravelDetailSection>

                    <TravelDetailSection title={`Trip Logistics (${services.length})`}>
                        {services.length === 0 ? (
                            <EmptyState message="No trip logistics added." />
                        ) : services.map((s, i) => (
                            <SectionCard
                                key={i}
                                title={s.service_name || `Trip Logistics ${i + 1}`}
                                chips={[
                                    s.category && (
                                        <Chip key="cat" label={formatEnumLabel(s.category)} size="small" variant="outlined" />
                                    ),
                                    s.sub_category && (
                                        <Chip key="sub" label={formatEnumLabel(s.sub_category)} size="small" variant="outlined" />
                                    ),
                                    s.status && (
                                        <StatusChip key="status" value={s.status} />
                                    ),
                                ].filter(Boolean)}
                            >
                                <Grid container spacing={1.5}>
                                    <Field label="Cost Price" value={s.cost_price != null && s.cost_price !== '' ? `${(s.currency || currency)?.toUpperCase()} ${s.cost_price}` : null} />
                                    <Field label="Description" value={s.description} xs={12} html />
                                    {s.category === 'accommodation' && <>
                                        <Field label="Property Name" value={s.property_name} />
                                        <Field label="Property Address" value={s.property_address} />
                                        <Field label="Check In" value={s.check_in_time} />
                                        <Field label="Check Out" value={s.check_out_time} />
                                        <Field label="Min Occupancy" value={s.min_occupancy} />
                                        <Field label="Max Occupancy" value={s.max_occupancy} />
                                    </>}
                                    {s.category === 'transportation' && <>
                                        <Field label="Pickup Location" value={s.pickup_location} />
                                        <Field label="Drop Location" value={s.drop_location} />
                                        <Field label="Pickup Time" value={s.pickup_time} />
                                        <Field label="Drop Time" value={s.drop_time} />
                                        <Field label="Min Passengers" value={s.min_passenger} />
                                        <Field label="Max Passengers" value={s.max_passenger} />
                                        {s.sub_category === 'flight' && <>
                                            <Field label="Airline" value={s.airline} />
                                            <Field label="Flight Number" value={s.flight_number} />
                                            <Field label="Baggage Included" value={s.baggage_included ? 'Yes' : 'No'} />
                                            <Field label="Departure Airport" value={s.departure_airport} />
                                            <Field label="Arrival Airport" value={s.arrival_airport} />
                                            <Field label="Departure Time" value={s.departure_time} />
                                            <Field label="Arrival Time" value={s.arrival_time} />
                                        </>}
                                    </>}
                                    {s.category === 'activity' && <>
                                        <Field label="Activity Location" value={s.activity_location} xs={12} />
                                        <Field label="Start Time" value={s.start_time} />
                                        <Field label="End Time" value={s.end_time} />
                                        <Field label="Duration (hrs)" value={getActivityDuration(s)} />
                                        <Field label="Min Participants" value={s.minimum_participants} />
                                        <Field label="Max Participants" value={s.maximum_participants} />
                                    </>}
                                    {s.category === 'meal' && <>
                                        <Field label="Venue" value={s.venue} />
                                        <Field label="Service Time" value={s.service_time} />
                                    </>}
                                    {s.category === 'guide' && <>
                                        <Field label="Guide Name" value={s.guide_name} />
                                        <Field label="Contact Number" value={s.contact_number} />
                                        <Field label="Languages" value={s.languages} />
                                        <Field label="Reporting Time" value={s.reporting_time} />
                                        <Field label="Meeting Point" value={s.meeting_point} />
                                    </>}
                                </Grid>
                            </SectionCard>
                        ))}
                    </TravelDetailSection>

                    <TravelDetailSection title="Itinerary">
                        <Grid container spacing={2}>
                            <Field label="Title" value={itineraries.itinerary_title} />
                            <Field label="Inclusion" value={itineraries.inclusion} xs={12} html />
                            <Field label="Exclusion" value={itineraries.exclusion} xs={12} html />
                        </Grid>

                        {days.length > 0 && (
                            <Box mt={2}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                                    Daily Schedule ({days.length} days)
                                </MDTypography>
                                {days.map((day, di) => (
                                    <SectionCard
                                        key={di}
                                        title={`Day ${day.day_number || di + 1}${day.day_title ? `: ${day.day_title}` : ''}`}
                                    >
                                        {(day.line_items || []).length === 0 ? (
                                            <EmptyState message="No line items for this day." />
                                        ) : (day.line_items || []).map((item, li) => (
                                            <Box key={li} sx={{ mb: li < (day.line_items.length - 1) ? 1.5 : 0 }}>
                                                <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                                                    Line item {li + 1}
                                                </MDTypography>
                                                <Grid container spacing={1.5}>
                                                    <Field label="Title" value={item.item_title} />
                                                    <Field label="Location" value={item.location} />
                                                    <Field label="Sequence" value={item.sequence} />
                                                    <Field label="Notes" value={item.day_line_item_description} xs={12} html />
                                                </Grid>
                                            </Box>
                                        ))}
                                    </SectionCard>
                                ))}
                            </Box>
                        )}
                    </TravelDetailSection>

                    <TravelDetailSection title={`FAQs (${faqs.length})`}>
                        {faqs.length === 0 ? (
                            <EmptyState message="No FAQs added." />
                        ) : faqs.map((faq, i) => (
                            <SectionCard key={i} title={`FAQ ${i + 1}`}>
                                <Grid container spacing={1.5}>
                                    <Field label="Question" value={faq.question} xs={12} />
                                    <Field label="Answer" value={faq.answer} xs={12} />
                                    <Field label="Published" value={faq.active ? 'Yes' : 'No'} />
                                </Grid>
                            </SectionCard>
                        ))}
                    </TravelDetailSection>
                </Grid>

                {/* Right column */}
                <Grid item xs={12} lg={4}>
                    {heroImage && (
                        <MDBox
                            mb={3}
                            borderRadius="lg"
                            overflow="hidden"
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                component="img"
                                src={heroImage}
                                alt={`${tripTitle} banner`}
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: 480,
                                    display: 'block',
                                    objectFit: 'contain',
                                }}
                            />
                        </MDBox>
                    )}

                    <TravelDetailSection title={`Departures (${departures.length})`}>
                        {departures.length === 0 ? (
                            <EmptyState message="No departures added." />
                        ) : departures.map((d, i) => (
                            <SectionCard
                                key={i}
                                title={`Departure ${i + 1}`}
                                subtitle={[formatDate(d.departure_date), formatDate(d.return_date)].filter(Boolean).join(' → ')}
                                chips={[<StatusChip key="s" value={d.status} />]}
                            >
                                <Grid container spacing={1.5}>
                                    <Field label="Departure City" value={d.departure_city} xs={12} />
                                    {/*<Field label="Total Allocation" value={d.total_allocation} />*/}
                                    <Field label="Book By Date" value={formatDate(d.book_by_date)} />
                                    <Field label="Min Pax" value={d.minimum_pax} />
                                    <Field label="Max Pax" value={d.total_allocation} />
                                    {(d.core_discount || d.discount_percentage) && (
                                        <Field label="Discount (%)" value={d.core_discount || d.discount_percentage} xs={12} />
                                    )}
                                    {d.hss_template && <>
                                        <Field label="HSS Template" value={d.hss_template} />
                                        <Field label="HSS Flat Charge" value={d.hss_flat_charge} />
                                        <Field label="HSS Total" value={d.hss_total} />
                                    </>}
                                    <Field label="Early Bird Deal" value={d.enable_early_bird_deal ? 'Enabled' : 'Disabled'} xs={12} />
                                    {d.enable_early_bird_deal && <>
                                        <Field label="Early Bird Start" value={formatDate(d.early_bird_start_date)} />
                                        <Field label="Early Bird End" value={formatDate(d.early_bird_end_date)} />
                                    </>}
                                    <Field label="Last Minute Deal" value={d.enable_last_minute_deal ? 'Enabled' : 'Disabled'} xs={12} />
                                    {d.enable_last_minute_deal && <>
                                        <Field label="Last Minute Start" value={formatDate(d.last_minute_start_date)} />
                                        <Field label="Last Minute End" value={formatDate(d.last_minute_end_date)} />
                                    </>}
                                </Grid>
                            </SectionCard>
                        ))}
                    </TravelDetailSection>

                    <TravelDetailSection title={`Merchant Allocations (${allocations.length})`}>
                        {allocations.length === 0 ? (
                            <EmptyState message="No merchant allocations added." />
                        ) : allocations.map((a, i) => (
                            <SectionCard
                                key={a.id || i}
                                title={a.name || `Allocation ${i + 1}`}
                                chips={[<StatusChip key="s" value={a.status || 'Pending'} />]}
                            >
                                <Grid container spacing={1.5}>
                                        <Field label="Merchant" value={a.merchant_id} />
                                    <Field label="Pricing Model" value={a.pricing_model} />
                                    <Field label="Departure" value={a.departure_date_id} />
                                    <Field label="Trip Logistics" value={a.package_component_id} />
                                    <Field label="Currency" value={a.currency} />
                                    <Field label="Requested Allocation" value={a.min_number} />
                                    <Field label="Allocation Priority" value={a.allocation_priority} />
                                    <Field label="Merchant Cost" value={a.cost} />
                                    {(a.pricing_model || 'Dated') === 'Dated' && (
                                        <>
                                            <Field label="Date From" value={a.season_from} />
                                            <Field label="Date To" value={a.season_to} />
                                        </>
                                    )}
                                </Grid>
                            </SectionCard>
                        ))}
                    </TravelDetailSection>

                    <TravelDetailSection title={`Add-On (${addons.length})`}>
                        {addons.length === 0 ? (
                            <EmptyState message="No add-ons added." />
                        ) : addons.map((a, i) => (
                            <SectionCard
                                key={i}
                                title={a.addon_name || `Add-on ${i + 1}`}
                                chips={[
                                    a.addon_type && (
                                        <Chip key="type" label={formatEnumLabel(a.addon_type)} size="small" variant="outlined" />
                                    ),
                                    a.status && (
                                        <StatusChip key="status" value={a.status} />
                                    ),
                                ].filter(Boolean)}
                            >
                                <Grid container spacing={1.5}>
                                    <Field label="Website Title" value={a.website_title} xs={12} />
                                    <Field label="Scope" value={a.application_scope} enumValue />
                                    <Field label="Price" value={a.addon_price ? `${(a.currency || currency)?.toUpperCase()} ${a.addon_price}` : null} />
                                    <Field label="Pricing Basis" value={a.pricing_basis || 'per_passenger'} enumValue />
                                    <Field label="Min Pax" value={a.min_quantity} />
                                    <Field label="Max Pax" value={a.max_quantity} />
                                    <Field label="Website Description" value={a.website_description} xs={12} html />
                                    {(a.addon_type === 'arrive_early' || a.addon_type === 'stay_behind') && <>
                                        <Field
                                            label="Number of Days"
                                            value={
                                                a.number_of_days
                                                || (a.addon_itinerary_days || []).length
                                                || a.extension_days_count
                                                || a.max_extension_days
                                                || a.min_extension_days
                                            }
                                        />
                                    </>}
                                    {(a.addon_itinerary_days || []).length > 0 && (
                                        <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                                                Extension Day Schedule
                                            </MDTypography>
                                            {(a.addon_itinerary_days || []).map((d, di) => (
                                                <Box key={d.id || di} sx={{ mb: 1.5, pl: 1, borderLeft: '3px solid #1565C8' }}>
                                                    <MDTypography variant="button" fontWeight="medium">
                                                        Day {d.day_number}: {d.day_title || 'Untitled'}
                                                    </MDTypography>
                                                    {(d.line_items || []).length > 0 && (
                                                        <MDTypography variant="caption" color="text" display="block">
                                                            {(d.line_items || []).length} line item{(d.line_items || []).length === 1 ? '' : 's'}
                                                        </MDTypography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Grid>
                            </SectionCard>
                        ))}
                    </TravelDetailSection>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ReviewStep;
